import EntryPoint from "@components/entry_point";
import { Body, Link, SectionHeader } from "@sprout/typography";
import useTranslation from "@hooks/use_translation";
import LegalLinks from "@components/legal_links.tsx";
import Container from "@components/ui/container.tsx";
import useProfile from "@/queries/use_profile";
import LoadingSpinner from "@/sprout/loading_spinner";
import { useSalaryOptions } from "@hooks/job_search/use_filter_options.ts";

const Profile = () => {
  const { t } = useTranslation("user");
  const { t: tMisc } = useTranslation("misc");
  const { data, isLoading, error } = useProfile();
  const salaryOptions = useSalaryOptions();

  const ProfileHeader = () => (
    <SectionHeader medium as="h1">
      {t("profile")}
    </SectionHeader>
  );

  if (isLoading) {
    return (
      <Container>
        <ProfileHeader />
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="medium" />
        </div>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container>
        <ProfileHeader />
        <Body>{t("defaultError")}</Body>
      </Container>
    );
  }

  const {
    email,
    phoneNumber,
    preferredName,
    location,
    accountCompletion,
    quickApplyCompletion,
    workPreferencesCompletion,
    workPreferences,
    isClearVerified,
    hasPendingClearVerificationTask,
  } = data;

  const firstName = data.firstName || "";
  const lastName = data.lastName || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const displayName = [firstName, preferredName?.trim() && `(${preferredName.trim()})`, lastName]
    .filter(Boolean)
    .join(" ");

  const worksitesLine =
    workPreferences.worksites.join(", ") +
    (workPreferences.willingToRelocate ? ` - ${t("workPreferences.willingToRelocate")}` : "");
  const salaryLabel =
    salaryOptions.find((option) => option.value === workPreferences.salary)?.label ?? "";
  const workPreferencesList = [
    workPreferences.employmentTypes.join(", "),
    worksitesLine,
    salaryLabel,
  ].filter((entry) => entry.length > 0);

  return (
    <Container>
      <ProfileHeader />
      <EntryPoint
        href="/account"
        sectionHeader={t("account")}
        fullName={fullName}
        isClearVerified={isClearVerified}
        email={email}
        completionStatus={accountCompletion}
        showClearActionRequired={hasPendingClearVerificationTask}
      />
      <EntryPoint
        href="/quick_apply"
        sectionHeader={t("quickApply")}
        fullName={displayName}
        phoneNumber={phoneNumber}
        location={location?.display_name}
        description={t("quick_apply_description")}
        showDescription={!phoneNumber && !location?.display_name && !displayName.trim()}
        completionStatus={quickApplyCompletion}
      />
      <EntryPoint
        href="/work_preferences"
        sectionHeader={t("workPreferences.title")}
        completionStatus={workPreferencesCompletion}
        description={t("work_preferences_description")}
        workPreferences={workPreferencesList}
        showDescription={workPreferencesList.length <= 0}
      />

      <div>
        <div className="text-center pb-4">
          <Body>
            <Link href="https://my.greenhouse.com/" external>
              {tMisc("about")}
            </Link>
          </Body>
        </div>

        <LegalLinks />
      </div>
    </Container>
  );
};

export default Profile;
